/**
 * Correlation matrix renderer: an `n x n` grid of pairwise coefficients.
 *
 * Structurally this is `render-heatmap.ts` with the binning taken out. The cells
 * are painted in a uPlot `draw` hook over an invisible series, which is what
 * keeps real axes, the resize machinery, grid panels, dashboard tiles and image
 * export all working without a second implementation of any of them.
 *
 * Three things differ from a heatmap, and they are the whole design:
 *
 * 1. **The axes are categorical.** Ticks sit at cell centres and read as column
 *    names, so the grid is addressed by signal rather than by value.
 * 2. **The ramp is signed and fixed.** Correlation lives in `[-1, +1]` with zero
 *    as the meaningful middle, so the scale is pinned to that range and wants a
 *    diverging ramp - a sequential one cannot show the sign, which is usually
 *    the first thing being asked.
 * 3. **The number is drawn in the cell** wherever it fits. A colour tells you
 *    there is a relationship; only the figure tells you how much, and a matrix
 *    is read as a table as often as it is read as a picture.
 *
 * An empty cell is never a zero. It is a pair with too few complete rows, or a
 * column that never moved - both hatched rather than coloured, and both named in
 * the tooltip, because a flat sensor reading as "uncorrelated" is the kind of
 * wrong that gets believed.
 */

import type uPlot from 'uplot';
import {
  plotState,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MIN,
  type PlotState,
} from '../../../state/plot-state.ts';
import { colorScaleGradientCss } from '../plot-constants.ts';
import { colorScaleRgb } from './color-scale.ts';
import { runtime } from '../plot-runtime.ts';
import { colorLegend } from '../plot-ui-state.ts';
import { themeColors, chartContainerSize, type ThemeColors } from './chart-dom.ts';
import { buildXAxis, buildYAxis, rebuildUplot } from './uplot-helpers.ts';
import { escHtml } from './format.ts';
import type { CorrelationResponse } from '../../../api/types.ts';
import { placeTooltip } from './tooltip.ts';

/** Quantized ramp: one `fillStyle` per bucket rather than one per cell. */
const RAMP_BUCKETS = 64;

/** Cell separators are drawn only once a cell is big enough to have an inside. */
const SEPARATOR_MIN_PX = 10;

/**
 * Cell sizes at which the figure fits, as multiples of the text size: below the
 * first there is no room to print it at all, and between the two only one
 * decimal fits. Relative rather than fixed, so raising the chart's text size
 * drops the second decimal before it starts overprinting the cell.
 */
const LABEL_MIN_RATIO = 2.3;
const LABEL_2DP_RATIO = 3.4;

/** Roughly how wide a label character is per pixel of font size. */
const LABEL_CHAR_RATIO = 0.68;

/**
 * Caps on the axis gutters at the default text size, so one long signal name
 * cannot eat the chart. Larger text buys a proportionally larger gutter - up to
 * a point - rather than simply losing more of every name.
 */
const X_AXIS_MAX_PX = 165;
const Y_AXIS_MAX_PX = 195;
const GUTTER_GROWTH_MAX = 1.4;

function gutterCap(base: number, fontPx: number): number {
  const growth = Math.min(GUTTER_GROWTH_MAX, Math.max(1, fontPx / FONT_SIZE_DEFAULT));
  return Math.round(base * growth);
}

/**
 * X labels are rotated. The sign matters: uPlot right-aligns a positively
 * rotated label, so the text ends at its tick and runs back under the axis
 * instead of off the right-hand edge of the canvas past the last column.
 */
const X_LABEL_ROTATE_DEG = 38;
const X_ROTATE_SIN = Math.sin((Math.abs(X_LABEL_ROTATE_DEG) * Math.PI) / 180);

/**
 * Axis font size for `n` signals, starting from the chart's text setting.
 *
 * A 30-signal matrix has cells narrower than a label is tall, so the font gives
 * way rather than the labels - an unlabelled row of a correlation matrix says
 * nothing at all. Up to about fifteen signals the text is exactly the size every
 * other chart uses; past that it steps down, and never below the readable floor.
 */
export function axisFontPx(n: number, basePx = FONT_SIZE_DEFAULT): number {
  const drop = n > 24 ? 3 : n > 14 ? 2 : 0;
  return Math.max(FONT_SIZE_MIN, basePx - drop);
}

/**
 * How many characters of a signal name fit the gutter at this font size.
 *
 * Derived rather than fixed, so shrinking the font for a wide matrix buys more
 * of the name rather than the same truncation in smaller type.
 */
export function labelMaxChars(fontPx: number, gutterPx: number): number {
  return Math.max(8, Math.floor((gutterPx - 16) / (fontPx * LABEL_CHAR_RATIO)));
}

/**
 * A signal name shortened to `max` characters, with the middle elided.
 *
 * Signal names run long (`AZIPODS_TOT_ELECTRIC_POWER`) and a gutter wide enough
 * for the longest would be most of the chart. The ellipsis goes in the middle
 * because both ends distinguish: `POD1_…_POWER` against `POD2_…_POWER`. The
 * tooltip always carries the full name.
 */
export function fitLabel(name: string, max: number): string {
  if (name.length <= max) return name;
  const head = Math.ceil((max - 1) / 2);
  const tail = max - 1 - head;
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

/**
 * Ramp position for a coefficient: `-1` at the bottom of the ramp, `0` in its
 * middle, `+1` at the top.
 *
 * Fixed rather than fitted to the data on purpose. A matrix whose strongest pair
 * is 0.3 must not be painted as though 0.3 were a perfect correlation - the
 * whole point of the fixed domain is that two matrices are comparable, and that
 * "nearly white" always means "nearly nothing".
 */
export function corrRampPosition(r: number): number {
  if (!Number.isFinite(r)) return 0.5;
  const clamped = r < -1 ? -1 : r > 1 ? 1 : r;
  return (clamped + 1) / 2;
}

/** The figure drawn inside a cell, at the precision the cell can hold. */
export function corrCellLabel(r: number, cellPx: number, fontPx = FONT_SIZE_DEFAULT): string {
  if (cellPx < fontPx * LABEL_MIN_RATIO) return '';
  if (Math.abs(r) >= 0.995) return r > 0 ? '1' : '-1';
  return cellPx >= fontPx * LABEL_2DP_RATIO ? r.toFixed(2) : r.toFixed(1);
}

/**
 * Whether a cell is drawn at all.
 *
 * The matrix is symmetric, so the upper triangle repeats the lower one. Showing
 * it is the default - every cell is then hoverable, and the square is the shape
 * people recognise - but the halved form is easier to read along a row once
 * there are more than a dozen signals.
 */
export function corrCellVisible(row: number, col: number, triangleOnly: boolean): boolean {
  return triangleOnly ? col <= row : true;
}

/** Readable text over a filled cell: dark on the pale middle, light on the ends. */
function textOn(rgb: string): string {
  const m = /rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(rgb);
  if (!m) return '#ffffff';
  const [r, g, b] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Perceived brightness (ITU-R BT.601), which is enough to pick one of two.
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#12151a' : '#ffffff';
}

/**
 * Integer pixel boundaries for the `n + 1` cell edges of one axis.
 *
 * Rounded once, here, so neighbouring cells share exact boundaries instead of
 * each rounding its own width and leaving hairline seams - the same reason
 * `render-heatmap.ts` does it.
 */
function pixelEdges(u: uPlot, n: number, scale: 'x' | 'y'): number[] {
  const out: number[] = [];
  for (let i = 0; i <= n; i++) out.push(Math.round(u.valToPos(i, scale, true)));
  return out;
}

/**
 * Matrix row `i` counts down from the top, the way a table is read; the y scale
 * counts up from the bottom. This is the one conversion between them.
 */
function rowToCell(i: number, n: number): number {
  return n - 1 - i;
}

/** A pair's coefficient, or null where there is nothing to draw. */
function valueAt(data: CorrelationResponse, row: number, col: number): number | null {
  const v = data.values[row * data.n + col];
  return v == null || !Number.isFinite(v) ? null : v;
}

/** Hatching for a cell that has no coefficient - visibly not a zero. */
function hatchCell(
  ctx: CanvasRenderingContext2D,
  c: ThemeColors,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.globalAlpha = 0.5;
  ctx.strokeStyle = c.text;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let o = -h; o < w; o += 7) {
    ctx.moveTo(x + o, y + h);
    ctx.lineTo(x + o + h, y);
  }
  ctx.stroke();
  ctx.restore();
}

/** The draw hook that paints the matrix. Shared by the chart, panels and tiles. */
export function correlationDrawHook(
  data: CorrelationResponse,
  c: ThemeColors,
  state?: PlotState,
): (u: uPlot) => void {
  const s = state ?? plotState.value;
  const triangle = s.style.corr_triangle;
  const scaleName = s.color_scale || 'CoolWarm';
  const ramp: string[] = [];
  for (let b = 0; b < RAMP_BUCKETS; b++) {
    ramp.push(colorScaleRgb(scaleName, (b + 0.5) / RAMP_BUCKETS));
  }
  const labelColors = ramp.map(textOn);

  return (u: uPlot): void => {
    const ctx = u.ctx;
    const n = data.n;
    if (n < 1) return;
    const xp = pixelEdges(u, n, 'x');
    const yp = pixelEdges(u, n, 'y');
    const { left, top, width, height } = u.bbox;

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();

    // The empty field first, so cells sit in a matrix rather than floating.
    ctx.fillStyle = c.grid;
    ctx.globalAlpha = 0.18;
    ctx.fillRect(
      Math.min(xp[0]!, xp[n]!),
      Math.min(yp[0]!, yp[n]!),
      Math.abs(xp[n]! - xp[0]!),
      Math.abs(yp[n]! - yp[0]!),
    );
    ctx.globalAlpha = 1;

    const cw = Math.abs(xp[n]! - xp[0]!) / n;
    const ch = Math.abs(yp[n]! - yp[0]!) / n;
    const labelPx = Math.min(cw, ch);
    // The chart's text size is what a cell's figure aims for; a cell too small
    // to hold it shrinks the number rather than dropping it early.
    const basePx = s.style.font_size;
    const fontPx = Math.max(
      FONT_SIZE_MIN,
      Math.min(basePx, Math.round(labelPx * 0.34)),
    );
    ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let row = 0; row < n; row++) {
      const cell = rowToCell(row, n);
      const yTop = Math.min(yp[cell]!, yp[cell + 1]!);
      const yBot = Math.max(yp[cell]!, yp[cell + 1]!);
      for (let col = 0; col < n; col++) {
        if (!corrCellVisible(row, col, triangle)) continue;
        const xLeft = Math.min(xp[col]!, xp[col + 1]!);
        const xRight = Math.max(xp[col]!, xp[col + 1]!);
        const w = Math.max(1, xRight - xLeft);
        const h = Math.max(1, yBot - yTop);
        const v = valueAt(data, row, col);
        if (v == null) {
          hatchCell(ctx, c, xLeft, yTop, w, h);
          continue;
        }
        const bucket = Math.min(
          RAMP_BUCKETS - 1,
          (corrRampPosition(v) * RAMP_BUCKETS) | 0,
        );
        ctx.fillStyle = ramp[bucket]!;
        ctx.fillRect(xLeft, yTop, w, h);
        const label = corrCellLabel(v, labelPx, basePx);
        if (label) {
          ctx.fillStyle = labelColors[bucket]!;
          ctx.fillText(label, xLeft + w / 2, yTop + h / 2);
        }
      }
    }

    // Separators on the same integer boundaries as the fills.
    if (cw >= SEPARATOR_MIN_PX && ch >= SEPARATOR_MIN_PX) {
      ctx.strokeStyle = c.grid;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      for (let i = 0; i <= n; i++) {
        const x = xp[i]! + 0.5;
        ctx.moveTo(x, Math.min(yp[0]!, yp[n]!));
        ctx.lineTo(x, Math.max(yp[0]!, yp[n]!));
        const y = yp[i]! + 0.5;
        ctx.moveTo(Math.min(xp[0]!, xp[n]!), y);
        ctx.lineTo(Math.max(xp[0]!, xp[n]!), y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  };
}

/** Cell under the cursor as `(row, col)` indices, or null when outside. */
export function correlationCellAt(
  u: uPlot,
  n: number,
): { row: number; col: number } | null {
  const left = u.cursor.left;
  const top = u.cursor.top;
  if (left == null || top == null || left < 0 || top < 0) return null;
  const col = Math.floor(u.posToVal(left, 'x'));
  const cell = Math.floor(u.posToVal(top, 'y'));
  if (col < 0 || col >= n || cell < 0 || cell >= n) return null;
  return { row: rowToCell(cell, n), col };
}

/** Why a cell is empty, in the words the tooltip should use. */
export function emptyCellReason(
  data: CorrelationResponse,
  row: number,
  col: number,
): string {
  if (data.constant_columns[row] || data.constant_columns[col]) {
    const flat = data.constant_columns[row] ? data.columns[row] : data.columns[col];
    return `${flat} never varies - a coefficient is undefined, not zero`;
  }
  const n = data.counts[row * data.n + col] ?? 0;
  return `only ${n.toLocaleString()} rows carry both - under the ${data.min_samples.toLocaleString()} needed`;
}

/**
 * Cell readout. Written straight into the tooltip node rather than through
 * `updateTooltip`, which keys everything off a series index - this chart has no
 * series, and the thing under the pointer is a pair of signals.
 */
export function correlationTooltipHook(
  data: CorrelationResponse,
  target?: { el: HTMLElement; container: HTMLElement },
  drillable = false,
): (u: uPlot) => void {
  return (u: uPlot): void => {
    const tt = target?.el ?? runtime.tooltipEl;
    if (!tt) return;
    const cell = correlationCellAt(u, data.n);
    if (!cell || !u.over) {
      tt.classList.remove('visible');
      tt.dataset.ttIdx = '';
      return;
    }
    const { row, col } = cell;
    const key = `corr:${row}:${col}`;
    if (tt.dataset.ttIdx !== key) {
      const v = valueAt(data, row, col);
      const pairs = data.counts[row * data.n + col] ?? 0;
      const method = data.method === 'spearman' ? 'Spearman ρ' : 'Pearson r';
      const rows = [
        `<div class="uptt-time">${escHtml(data.columns[row] ?? '')} × ${escHtml(data.columns[col] ?? '')}</div>`,
      ];
      if (v == null) {
        rows.push(
          `<div class="uptt-row"><span class="uptt-name">no value</span>` +
            `<span class="uptt-val">${escHtml(emptyCellReason(data, row, col))}</span></div>`,
        );
      } else {
        rows.push(
          `<div class="uptt-row"><span class="uptt-name">${method}</span>` +
            `<span class="uptt-val">${v.toFixed(3)}</span></div>`,
        );
      }
      rows.push(
        `<div class="uptt-row"><span class="uptt-name">rows</span>` +
          `<span class="uptt-val">${pairs.toLocaleString()}</span></div>`,
      );
      // A coefficient only measures a straight line (or, ranked, a consistent
      // direction) - the pair itself is one click away, which is the honest
      // answer to "is that 0.1 really nothing?".
      if (drillable && row !== col) {
        rows.push('<div class="uptt-time">click to open the pair as a scatter plot</div>');
      }
      tt.innerHTML = rows.join('');
      tt.dataset.ttIdx = key;
    }
    tt.classList.add('visible');

    const container = target?.container ?? runtime.containerEl;
    if (container) placeTooltip(u, tt, container);
  };
}

/** Gutter wide enough for the longest label as it will actually be drawn. */
function axisSize(
  labels: string[],
  fontPx: number,
  max: number,
  extra: number,
  scale = 1,
): number {
  const longest = labels.reduce((m, c) => Math.max(m, c.length), 0);
  return Math.min(max, Math.round(longest * fontPx * LABEL_CHAR_RATIO * scale) + extra);
}

/**
 * uPlot options for a correlation matrix. `size` and the tooltip target let a
 * grid panel or a dashboard tile reuse this drawing; `onPick` is Explore's
 * drill-in and is left off everywhere else.
 */
export function buildCorrelationOpts(
  data: CorrelationResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  target?: { el: HTMLElement; container: HTMLElement },
  state?: PlotState,
  onPick?: (a: string, b: string) => void,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const n = data.n;

  // One invisible series: uPlot needs an x array and at least one y array, and
  // the cells are painted by hand. The x values are cell centres, so the cursor
  // has something to snap to at each column.
  const centres: number[] = [];
  for (let i = 0; i < n; i++) centres.push(i + 0.5);
  const uData: uPlot.AlignedData = [centres, centres.map(() => null)];

  const fontPx = axisFontPx(n, (state ?? plotState.value).style.font_size);
  const axisFont = `${fontPx}px system-ui, -apple-system, sans-serif`;
  const yCap = gutterCap(Y_AXIS_MAX_PX, fontPx);
  const xCap = gutterCap(X_AXIS_MAX_PX, fontPx);
  const maxChars = labelMaxChars(fontPx, yCap);
  const shown = data.columns.map((col) => fitLabel(col, maxChars));

  const xAxis = buildXAxis(c, false, '');
  xAxis.splits = () => centres;
  xAxis.values = () => shown.slice();
  xAxis.rotate = X_LABEL_ROTATE_DEG;
  xAxis.font = axisFont;
  // Rotated, so the label's length turns into height - reserve that, or the
  // names run off the bottom of the canvas and are cut mid-word.
  xAxis.size = axisSize(shown, fontPx, xCap, 22, X_ROTATE_SIN);
  xAxis.grid = { show: false };

  const yAxis = buildYAxis(c, 'y', 'left', '');
  // Row 0 is the first column and sits at the top, so the labels come back in
  // reverse: the y scale counts up from the bottom.
  yAxis.splits = () => centres;
  yAxis.values = () => centres.map((_, i) => shown[rowToCell(i, n)] ?? '');
  yAxis.font = axisFont;
  yAxis.size = axisSize(shown, fontPx, yCap, 14);
  yAxis.grid = { show: false };

  const opts: uPlot.Options = {
    ...size,
    // Both scales are pinned to the cell grid: the only y values in the data are
    // nulls, and a correlation matrix has no zoom worth having.
    scales: {
      x: { time: false, range: (): [number, number] => [0, n] },
      y: { range: (): [number, number] => [0, n] },
    },
    axes: [xAxis, yAxis],
    series: [{}, { label: 'correlation', paths: () => null, points: { show: false } }],
    legend: { show: false },
    cursor: { x: true, y: true, drag: { x: false, y: false } },
    hooks: {
      draw: [correlationDrawHook(data, c, state)],
      setCursor: [correlationTooltipHook(data, target, !!onPick)],
    },
  };

  if (onPick) {
    opts.hooks!.ready = [
      (u: uPlot): void => {
        u.over.style.cursor = 'pointer';
        u.over.addEventListener('click', () => {
          const cell = correlationCellAt(u, n);
          if (!cell || cell.row === cell.col) return;
          const a = data.columns[cell.col];
          const b = data.columns[cell.row];
          if (a && b) onPick(a, b);
        });
      },
    ];
  }
  return { opts, uData };
}

/** Colour-ramp legend below the chart: always the full signed range. */
export function correlationLegend(data: CorrelationResponse): void {
  const method = data.method === 'spearman' ? 'Spearman ρ' : 'Pearson r';
  colorLegend.set({
    colorBy: method,
    min: -1,
    max: 1,
    gradientCss: colorScaleGradientCss(plotState.value.color_scale || 'CoolWarm'),
  });
}

export function renderCorrelation(
  data: CorrelationResponse,
  onPick?: (a: string, b: string) => void,
): void {
  const c = themeColors();
  const { opts, uData } = buildCorrelationOpts(
    data,
    c,
    chartContainerSize(),
    undefined,
    undefined,
    onPick,
  );
  rebuildUplot(uData, opts);
  correlationLegend(data);
}
